import type { HouseholdSnapshot } from '../domain/types';
import type { NestDatabase } from './local-db';

export async function applySnapshot(db: NestDatabase, snapshot: HouseholdSnapshot): Promise<boolean> {
  const key = `revision:${snapshot.household.id}`;
  return db.transaction('rw', [db.meta, db.outbox, db.households, db.memberships, db.categories, db.paymentMethods, db.transactions, db.budgets, db.recurringTemplates, db.occurrences, db.notifications], async () => {
    const current = await db.meta.get(key);
    if (current && BigInt(String(current.value)) > BigInt(snapshot.revision)) return false;
    const minimum = await db.meta.get(`minimumRevision:${snapshot.household.id}`);
    if (minimum && BigInt(String(minimum.value)) > BigInt(snapshot.revision)) return false;
    const householdId = snapshot.household.id;
    await Promise.all([
      db.households.put(snapshot.household),
      replaceHouseholdRows(db.memberships, householdId, snapshot.memberships),
      replaceHouseholdRows(db.categories, householdId, snapshot.categories),
      replaceHouseholdRows(db.paymentMethods, householdId, snapshot.paymentMethods),
      replaceHouseholdRows(db.transactions, householdId, snapshot.transactions),
      replaceHouseholdRows(db.budgets, householdId, snapshot.budgets),
      replaceHouseholdRows(db.recurringTemplates, householdId, snapshot.recurringTemplates),
      replaceHouseholdRows(db.occurrences, householdId, snapshot.recurringOccurrences),
      replaceHouseholdRows(db.notifications, householdId, snapshot.notifications)
    ]);
    await db.meta.put({ key, value: snapshot.revision });
    // Keep the optimistic view until its authoritative replacement is committed.
    await db.outbox.where('[householdId+state]').equals([householdId, 'acknowledged'])
      .filter((operation) => operation.acknowledgedRevision !== undefined && BigInt(operation.acknowledgedRevision) <= BigInt(snapshot.revision)).delete();
    await db.meta.put({ key: `lastSync:${householdId}`, value: new Date().toISOString() });
    return true;
  });
}

async function replaceHouseholdRows<T extends { householdId: string }>(table: { where(index: string): { equals(value: string): { delete(): Promise<number> } }; bulkPut(rows: T[]): Promise<unknown> }, householdId: string, rows: T[]) {
  await table.where('householdId').equals(householdId).delete();
  if (rows.length) await table.bulkPut(rows);
}
